import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Database,
  Download,
  Mail,
  Loader2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  Globe,
  Newspaper,
  User,
  Building2
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DatabaseViewerPage = () => {
  const { token, isMainUser } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sourceTypeFilter, setSourceTypeFilter] = useState('all');
  const [backupStatus, setBackupStatus] = useState(null);
  const [sendingBackup, setSendingBackup] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchData();
    fetchBackupStatus();
  }, [page, sourceTypeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (sourceTypeFilter !== 'all') {
        params.append('source_type', sourceTypeFilter);
      }
      
      const response = await axios.get(
        `${API_URL}/api/database/property-sources?${params}`,
        axiosConfig
      );
      setData(response.data.data);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/backup/status`, axiosConfig);
      setBackupStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch backup status');
    }
  };

  const handleSendBackup = async () => {
    setSendingBackup(true);
    try {
      const response = await axios.post(`${API_URL}/api/backup/send-now`, {}, axiosConfig);
      toast.success('Backup email sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send backup');
    } finally {
      setSendingBackup(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/backup/download-csv`, {
        ...axiosConfig,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sourceflow_backup_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded successfully');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const getSourceTypeIcon = (type) => {
    switch (type) {
      case 'PORTAL': return <Globe className="h-4 w-4 text-blue-500" />;
      case 'NEWSPAPER': return <Newspaper className="h-4 w-4 text-purple-500" />;
      case 'OWNER_REFERENCE': return <User className="h-4 w-4 text-teal-500" />;
      case 'CUSTOM_PROVIDER': return <Building2 className="h-4 w-4 text-amber-500" />;
      default: return <Database className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSourceTypeBadge = (type) => {
    const colors = {
      PORTAL: 'bg-blue-100 text-blue-800',
      NEWSPAPER: 'bg-purple-100 text-purple-800',
      OWNER_REFERENCE: 'bg-teal-100 text-teal-800',
      CUSTOM_PROVIDER: 'bg-amber-100 text-amber-800'
    };
    return <Badge className={colors[type] || 'bg-gray-100 text-gray-800'}>{type}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="database-viewer-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Database className="h-6 w-6" />
              Database Viewer
            </h1>
            <p className="text-muted-foreground">View all stored property sources</p>
          </div>
          
          {isMainUser && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadCSV}
                data-testid="download-csv-button"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download CSV
              </Button>
              <Button
                onClick={handleSendBackup}
                disabled={sendingBackup}
                data-testid="send-backup-button"
                className="gap-2"
              >
                {sendingBackup ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Send Backup Email
              </Button>
            </div>
          )}
        </div>

        {/* Backup Status Card */}
        {backupStatus && (
          <Card className="border-border bg-muted/30">
            <CardContent className="pt-4">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="font-medium">Auto Backup:</span>
                  <span className="text-muted-foreground">{backupStatus.schedule}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-500" />
                  <span className="font-medium">Email:</span>
                  <span className="text-muted-foreground">{backupStatus.backup_email}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="text-lg">
                Property Sources ({total.toLocaleString()} records)
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select value={sourceTypeFilter} onValueChange={(val) => { setSourceTypeFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-[180px]" data-testid="source-type-filter">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="PORTAL">Portal</SelectItem>
                    <SelectItem value="NEWSPAPER">Newspaper</SelectItem>
                    <SelectItem value="OWNER_REFERENCE">Owner Reference</SelectItem>
                    <SelectItem value="CUSTOM_PROVIDER">Custom Provider</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-button">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No records found</p>
              </div>
            ) : (
              <>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Source Type</TableHead>
                          <TableHead>Source Name</TableHead>
                          <TableHead>Raw Value</TableHead>
                          <TableHead>Source UID</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.map((item, index) => (
                          <TableRow key={item.id} className="hover:bg-muted/30">
                            <TableCell className="text-muted-foreground text-sm">
                              {(page - 1) * 50 + index + 1}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getSourceTypeIcon(item.source_type)}
                                <span className="text-sm">{item.source_type}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{item.source_name || '-'}</TableCell>
                            <TableCell className="max-w-[200px] truncate font-mono text-xs" title={item.raw_value}>
                              {item.raw_value}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-secondary">{item.source_uid}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{item.source_date || '-'}</TableCell>
                            <TableCell>
                              <Badge className={item.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}>
                                {item.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({total.toLocaleString()} total records)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-button"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-button"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DatabaseViewerPage;
